import { useEffect, useMemo, useRef, useState } from "react";

import { OrganizationsService } from "@/modules/dashboard/cards/services/organizationsService";
import type { Organization } from "@/modules/dashboard/cards/types/organizations-api.types";

const MAX_LOOKUPS = 50;

/**
 * Names for organization ids the preloaded catalog page does not hold. The search endpoint matches ids too,
 * so each missing id is one exact lookup, attempted once per page life; a miss keeps its uuid fallback.
 */
export function useOrganizationNames(
  catalog: Organization[],
  ids: (string | null)[],
): Record<string, string> {
  const [resolved, setResolved] = useState<Record<string, string>>({});
  const attempted = useRef(new Set<string>());

  const known = useMemo(() => {
    const map: Record<string, string> = {};
    for (const organization of catalog) map[organization.id] = organization.name;
    return map;
  }, [catalog]);

  const missingKey = [...new Set(ids.filter((id): id is string => !!id && !known[id]))].sort().join(",");

  useEffect(() => {
    if (!missingKey) return;
    const pending = missingKey
      .split(",")
      .filter((id) => !attempted.current.has(id))
      .slice(0, Math.max(0, MAX_LOOKUPS - attempted.current.size));
    for (const id of pending) {
      attempted.current.add(id);
      OrganizationsService.fetchOrganizations({ skip: 0, limit: 5, search: id })
        .then((response) => {
          const match = (response.data.data ?? []).find((organization) => organization.id === id);
          if (match) setResolved((current) => ({ ...current, [id]: match.name }));
        })
        .catch(() => undefined);
    }
  }, [missingKey]);

  return useMemo(() => ({ ...resolved, ...known }), [resolved, known]);
}
